<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <AppFormWrapper id="sign-in-contact-form" @submit.prevent="onSubmit">
    <AppModalDialogLoading :is-busy="!isClerkReady" busy-message="Please wait…" />
    <!-- always this title, wherever the dialog opens from (Seán, 2026-10-23) -->
    <h2 ref="titleRef">Welcome to VIZBI</h2>
    <!-- a sign-in asked for by a download or a free video says why (useSignInRequest.js) -->
    <template v-if="message">
      <!-- eslint-disable-next-line vue/no-v-html -- our own lines (useSignInRequest.js) -->
      <p v-for="line in [message].flat()" :key="line" v-html="line" />
    </template>
    <!-- the usual two: a member signs in, anyone else signs up (Seán, 2026-10-23) -->
    <template v-else>
      <p>
        Already in the VIZBI community? Sign in to access your content and manage your profile.
      </p>
      <p>Ready to join? Sign up to access {{ freeVideos }} and receive updates.</p>
    </template>
    <input
      ref="emailInput"
      v-model="email"
      type="email"
      placeholder="Your email address"
      autocomplete="email"
      required />
    <p v-if="errorMessage" class="error">{{ useTitleCase(errorMessage) }}</p>
  </AppFormWrapper>
</template>

<script setup>
import {fetchIsSessionActivated} from "@/composables/useSignInRecovery";
import {computed, ref, watch} from "vue";
import {useStorage} from "@vueuse/core";
import {useClerk, useSignIn, useSignUp} from "@clerk/vue";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import AppModalDialogLoading from "@/components/App/Modal/Dialog/Loading";
import {waitForValue} from "@/composables/waitForValue";
import {useTitleCase} from "@/composables/useTitleCase";
import {useAutoFocus} from "@/composables/useAutoFocus";
import {useMaxFontSize} from "@/composables/useMaxFontSize";
import {useRoute} from "vue-router";
import {getPrefillEmail} from "@/composables/prefillEmail";
import useLibraryTotals from "@/composables/useLibraryTotals";

defineProps(["message"]);
const emit = defineEmits(["next", "success", "update:canSubmit"]);

const route = useRoute();
// the free videos' count: stats.json "free" (every video anyone can watch, signed in or not)
const {stats} = useLibraryTotals();
const freeVideos = computed(() => {
  const count = stats.value?.free?.videos;
  return count ? `${count} free videos` : "free videos";
});
const emailInput = ref(null);
// the email field takes the focus as the dialog opens (the form is in the page all along; #sign-in
// opens the dialog)
useAutoFocus(emailInput, () => route.hash === "#sign-in");
const titleRef = ref(null);
// the title on one line: 30px, smaller only where it doesn't fit (issue SI2)
const isOpen = () => route.hash === "#sign-in";
useMaxFontSize(titleRef, {maxFontSize: 30, fillsWidth: true, isActive: isOpen});

const {signIn} = useSignIn();
const clerk = useClerk();
const {signUp} = useSignUp();

const email = useStorage("sign-in-email", "");
// an email link's address (prefillEmail.js), as the dialog opens (2026-10-27)
watch(isOpen, (open) => {
  const stored = getPrefillEmail();
  if (open && stored) email.value = stored;
}, {immediate: true});
const loading = ref(false);
// Clerk not loaded yet (it can take ~10 s): the dialog opens waiting (busy) until Clerk's signIn
// and signUp are ready, so Continue can't be pressed too early
const isClerkReady = computed(() => Boolean(signIn.value && signUp.value));
const errorMessage = ref("");
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const canSubmit = computed(() => {
  return isClerkReady.value && !loading.value && emailRegex.test(email.value);
});
watch(canSubmit, (val) => emit("update:canSubmit", val), {immediate: true});

// a safety net: Continue is disabled until Clerk is ready, but should it get through earlier, it
// waits rather than fails
async function fetchIsClerkReady() {
  return (await waitForValue(signIn)) && (await waitForValue(signUp));
}

async function onSubmit() {
  errorMessage.value = "";
  loading.value = true;
  try {
    if (!(await fetchIsClerkReady())) {
      errorMessage.value = "Sign-in is unavailable. Please reload and try again.";
      return;
    }
    await signIn.value.create({identifier: email.value, strategy: "email_code"});
    emit("next", {email: email.value, isExistingUser: true});
  } catch (error) {
    const code = error?.errors?.[0]?.code;
    // already signed in (e.g. a code accepted without the dialog seeing it): on as signed in
    if (code === "session_exists" && (await fetchIsSessionActivated(clerk.value))) {
      return emit("success");
    }
    if (code === "form_identifier_not_found") {
      try {
        await signUp.value.create({emailAddress: email.value, strategy: "email_code"});
        emit("next", {email: email.value, isExistingUser: false});
      } catch (signUpError) {
        errorMessage.value =
          signUpError?.errors?.[0]?.message || "An error occurred. Please try again.";
      }
    } else {
      errorMessage.value = error?.errors?.[0]?.message || "An error occurred. Please try again.";
    }
  } finally {
    loading.value = false;
  }
}
</script>


<style scoped>
/* Seán's spec, 2026-10-22: 30px, fitted to one line (useMaxFontSize: no width of its own, so
   it never widens the dialog) */
h2 {
  width: 0;
  min-width: 100%;
  margin: 0 0 16px;
  font-size: 30px;
}
p {
  margin-bottom: 0;
}
p + p {
  margin-top: 0.6em;
}
input {
  margin: 22px 0 0;
}
input::placeholder {
  color: #767676; /* 4.5:1 on white */
}
</style>
